import React, { SetStateAction, useState } from 'react';
import * as Yup from 'yup';
import { Button, Checkbox, Dialog, DialogDrawerRenderProps, Group, useToast } from '@economic/taco';

import { t } from '../../../../../../../../shared/components/l10n/l10n';
import { Form, Field } from '../../../../../../../../shared/components/Form';
import internalApiClient from '../../../../../../../../shared/util/api/internalApiClient';
import { JournalTypeEnum } from '../../../../../../../../../types/internal-api';

interface Props {
    drawerProps: DialogDrawerRenderProps;
    refetchJournals: () => Promise<any>;
    setPreSelectedJournalName: React.Dispatch<SetStateAction<undefined | string>>;
}

const AddJournalDrawer = ({ drawerProps, refetchJournals, setPreSelectedJournalName }: Props) => {
    const toast = useToast();

    const [approvalRequired, setApprovalRequired] = useState(false);
    const [lockVAT, setLockVAT] = useState(false);

    const onFormSubmit = async values => {
        const { journalName } = values;

        try {
            await createJournal(journalName);

            refetchJournals ? await refetchJournals() : null;
            setPreSelectedJournalName(journalName);

            resetForm(values);
            drawerProps.toggle();
        } catch (e) {
            toast.error(t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_ERROR`);
        }
    };

    const createJournal = async (journalName: string) => {
        const journal = {
            Name: journalName,
            RequireAdminApproval: approvalRequired,
            LockVatCodes: lockVAT,
            Type: JournalTypeEnum.Classic,
        };

        const res = await internalApiClient.post('/journals', journal);

        if (res.status !== 200) throw new Error();
    };

    const resetForm = formValues => {
        formValues.journalName = '';
        setApprovalRequired(false);
        setLockVAT(false);
    };

    let journalValidationSchema = Yup.object({
        journalName: Yup.string().required(),
    });

    return (
        <>
            <Dialog.Title>
                <div className="mt-6 mb-4">{t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_TITLE`}</div>
            </Dialog.Title>

            <Form onSubmit={onFormSubmit} validationSchema={journalValidationSchema} skipNotifications>
                <div className="flex flex-col">
                    <span className="my-2">{t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_TEXT`}</span>

                    <div className="mt-3">
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_JOURNAL_NAME_INPUT_TITLE`}
                            name="journalName"
                        />
                    </div>

                    <Checkbox
                        className="my-2"
                        label={t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_APPROVAL_REQUIRED_INPUT_TITLE`}
                        name="approvalRequired"
                        checked={approvalRequired}
                        onChange={checked => setApprovalRequired(checked)}
                    />

                    <Checkbox
                        className="my-2"
                        label={t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_LOCK_VAT_INPUT_TITLE`}
                        name="lockVAT"
                        checked={lockVAT}
                        onChange={checked => setLockVAT(checked)}
                    />
                </div>

                <Dialog.Footer>
                    <Group>
                        <Button onClick={drawerProps.toggle}>{t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_CANCEL_BUTTON`}</Button>
                        <Button appearance="primary" type="submit">
                            {t`SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_SAVE_BUTTON`}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Form>
        </>
    );
};

export default AddJournalDrawer;
